import type { RevenueLedgerMonthlyResponse } from '@cortex/domain-ac-mens-hub-api';
import type { RevenueColumn, RevenueRecord } from '../types/revenue.js';
import type { MonthlyBucket } from './revenue-aggregation.js';
import { escapeCell } from './csv.js';

/**
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business revenue__daily のCSVエクスポート（日次/月次）、定義の版つき台帳の縦長CSV、rawデータCSV生成、rawデータ抽出用SQLテキスト生成、集計ロジック説明テキスト生成
 * @graph-connects csv [calls] escapeCell を再利用
 */

/** Excel 文字化け回避用の UTF-8 BOM（csv.ts と同一値）。 @graph-connects none */
const UTF8_BOM = '﻿';

/**
 * revenue__daily の日次行データを CSV 文字列に変換する。
 * @graph-connects none
 */
export function buildRevenueCsv(columns: RevenueColumn[], rows: RevenueRecord[]): string {
  const header = ['日付', ...columns.map((c) => c.label)].map(escapeCell);
  const body = rows.map((row) => {
    const values = [row.date, ...columns.map((c) => String(row[c.key]))];
    return values.map(escapeCell).join(',');
  });
  return `${UTF8_BOM}${[header.join(','), ...body].join('\r\n')}`;
}

/**
 * revenue__daily の月次集計データを CSV 文字列に変換する。
 * @graph-connects none
 */
export function buildMonthlyRevenueCsv(columns: RevenueColumn[], buckets: MonthlyBucket[]): string {
  const header = ['月', ...columns.map((c) => c.label)].map(escapeCell);
  const body = buckets.map((b) => {
    const values = [b.month, ...columns.map((c) => String(b.record[c.key]))];
    return values.map(escapeCell).join(',');
  });
  return `${UTF8_BOM}${[header.join(','), ...body].join('\r\n')}`;
}

/**
 * 定義の版つき台帳（月次）を縦長の CSV にする。1行 = 計上月×勘定×プラン×内訳。
 * どの版・どの朝の計算（その月の値を出した計算。締め済み月は締めた計算）の数字かを全行に入れる。値を持たない列は空欄。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 台帳の月次を、版と計算の番号つきの縦長CSVにする。経理が仕様SQLの結果と突き合わせるために使う。
 * @graph-connects csv [calls] escapeCell を再利用
 */
export function buildLedgerCsv(ledger: RevenueLedgerMonthlyResponse): string {
  const header = [
    '計上月',
    '勘定',
    'プラン',
    '内訳',
    '税抜',
    '消費税',
    '税込',
    '件数',
    '定義の版',
    'run_id',
  ].map(escapeCell);
  const runByMonth = new Map(ledger.monthRuns.map((r) => [r.month, r.runId]));
  const text = (value: number | null): string => (value === null ? '' : String(value));
  const body = ledger.rows.flatMap((row) =>
    row.cells.map((cell) =>
      [
        cell.month,
        row.bucket,
        row.planCode,
        row.breakdown,
        String(cell.amountNet),
        text(cell.consumptionTax),
        text(cell.amountInclTax),
        text(cell.bookedCount),
        ledger.version,
        runByMonth.get(cell.month) ?? '',
      ]
        .map(escapeCell)
        .join(','),
    ),
  );
  return `${UTF8_BOM}${[header.join(','), ...body].join('\r\n')}`;
}

/**
 * rawデータ行を汎用 CSV 文字列に変換する。
 * @graph-connects none
 */
export function buildRawDataCsv(columnNames: string[], rows: Record<string, unknown>[]): string {
  const header = columnNames.map(escapeCell);
  const body = rows.map((row) => {
    const values = columnNames.map((col) => {
      const v = row[col];
      if (v === null || v === undefined) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'number' || typeof v === 'boolean') return String(v);
      return JSON.stringify(v);
    });
    return values.map(escapeCell).join(',');
  });
  return `${UTF8_BOM}${[header.join(','), ...body].join('\r\n')}`;
}

/** generator の REVENUE_START_DATE_JST / RevenuePage SERIES_START と同一値。変更時は手動同期が必要。 @graph-connects none */
export const REVENUE_START_DATE = '2026-05-28';

/**
 * rawデータ抽出用の SQL クエリ一覧。
 * apps/generator/ac-mens-hub-daily/src/revenue/queries.ts のスナップショット。
 * generator 側の変更時は手動同期が必要。
 * @graph-connects none
 */
export const RAW_DATA_QUERIES: { key: string; label: string; database: string; sql: string }[] = [
  {
    key: 'payments',
    label: '課金履歴（aircloset_mens）',
    database: 'aircloset_mens (PostgreSQL)',
    sql: `SELECT
  ph.id,
  ph.user_id,
  pt.code AS payment_type_code,
  (ph.purchase_date AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::text AS purchase_date,
  ph.original_price,
  ph.phoenix_payment_history_id,
  COALESCE(
    (SELECT
      CASE
        WHEN sp.code IN ('monthly_monthly', 'annual_monthly') THEN 'monthly'
        WHEN sp.code IN ('monthly_bimonthly', 'annual_bimonthly') THEN 'bimonthly'
        ELSE NULL
      END
     FROM user_subscription_plan_relations uspr
     JOIN plan_ticket_price_versions ptpv ON ptpv.id = uspr.plan_ticket_price_version_id
     JOIN subscription_plans sp ON sp.id = ptpv.subscription_plan_id
     WHERE uspr.user_id = ph.user_id
       AND uspr.activated_at <= ph.purchase_date
     ORDER BY uspr.activated_at DESC NULLS LAST
     LIMIT 1
    ),
    (SELECT
      CASE
        WHEN sp2.code IN ('monthly_monthly', 'annual_monthly') THEN 'monthly'
        WHEN sp2.code IN ('monthly_bimonthly', 'annual_bimonthly') THEN 'bimonthly'
        ELSE NULL
      END
     FROM plan_ticket_price_versions ptpv2
     JOIN subscription_plans sp2 ON sp2.id = ptpv2.subscription_plan_id
     WHERE ptpv2.retail_price = ph.original_price
       AND ptpv2.subscription_plan_id IS NOT NULL
     ORDER BY ptpv2.id DESC
     LIMIT 1
    )
  ) AS plan_frequency
FROM payment_histories ph
JOIN payment_types pt ON pt.id = ph.payment_type_id
WHERE ph.purchase_date >= TIMESTAMP '${REVENUE_START_DATE}' AT TIME ZONE 'Asia/Tokyo' AT TIME ZONE 'UTC'
ORDER BY ph.purchase_date`,
  },
  {
    key: 'tickets',
    label: 'チケット情報（aircloset_mens）',
    database: 'aircloset_mens (PostgreSQL)',
    sql: `SELECT
  t.id,
  t.user_id,
  t.ticket_type,
  t.is_used,
  (t.expired_at AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::text AS expired_at,
  t.payment_history_id,
  t.order_id,
  CASE
    WHEN sp.code IN ('monthly_monthly', 'annual_monthly') THEN 'monthly'
    WHEN sp.code IN ('monthly_bimonthly', 'annual_bimonthly') THEN 'bimonthly'
    ELSE NULL
  END AS plan_frequency,
  (t.created_at AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::text AS created_at
FROM user_ticket_relations t
JOIN plan_ticket_price_versions ptpv ON ptpv.id = t.plan_ticket_price_version_id
LEFT JOIN subscription_plans sp ON sp.id = ptpv.subscription_plan_id
WHERE t.ticket_type IN ('subscription', 'purchase')
ORDER BY t.created_at`,
  },
  {
    key: 'cancellations',
    label: '退会情報（aircloset_mens）',
    database: 'aircloset_mens (PostgreSQL)',
    sql: `SELECT
  uspr.user_id,
  (uspr.cancelled_at AT TIME ZONE 'UTC' AT TIME ZONE 'Asia/Tokyo')::text AS cancelled_at
FROM user_subscription_plan_relations uspr
WHERE uspr.cancelled_at IS NOT NULL
  AND uspr.status = 'canceled'`,
  },
  {
    key: 'orders',
    label: '注文マッピング（aircloset_mens）',
    database: 'aircloset_mens (PostgreSQL)',
    sql: `SELECT id, phoenix_order_id FROM orders`,
  },
  {
    key: 'deliveries',
    label: '発送完了情報（phoenix_order）',
    database: 'phoenix_order (MySQL)',
    sql: `SELECT
  do2.order_id AS phoenix_order_id,
  DATE_FORMAT(CONVERT_TZ(do2.delivered_date, '+00:00', '+09:00'), '%Y-%m-%d %H:%i:%s') AS delivered_date
FROM delivery_orders do2
JOIN orders o ON o.id = do2.order_id
WHERE o.service_id = 3
  AND do2.order_status = 7
  AND do2.delivered_date IS NOT NULL`,
  },
  {
    key: 'payment_status',
    label: '決済ステータス（payment）',
    database: 'payment (MySQL)',
    sql: `-- min_id/max_id は aircloset_mens.payment_histories.phoenix_payment_history_id の範囲
SELECT
  id,
  paid_status,
  paid_flag,
  price
FROM user_payment_histories
WHERE id BETWEEN <min_id> AND <max_id>
-- 確定: paid_status=2 AND paid_flag=1
-- 失敗: paid_status=3 AND paid_flag=0`,
  },
  {
    key: 'refunds',
    label: '返金情報（payment）',
    database: 'payment (MySQL)',
    sql: `-- 通常返金
SELECT
  DATE_FORMAT(CONVERT_TZ(cph.canceled_date, '+00:00', '+09:00'), '%Y-%m-%d %H:%i:%s') AS canceled_date,
  cph.user_payment_history_id,
  uph.price
FROM cancel_payment_histories cph
JOIN user_payment_histories uph ON uph.id = cph.user_payment_history_id
JOIN payment_cycles pc ON pc.id = uph.payment_cycle_id
WHERE uph.id BETWEEN <min_id> AND <max_id>
  AND pc.service_id = 3

-- Amazon v1.1 返金（負price行）
SELECT
  uph.id, uph.paid_status, uph.paid_flag, uph.price,
  DATE_FORMAT(CONVERT_TZ(uph.created_at, '+00:00', '+09:00'), '%Y-%m-%d %H:%i:%s') AS created_at
FROM user_payment_histories uph
JOIN payment_cycles pc ON pc.id = uph.payment_cycle_id
WHERE uph.id BETWEEN <min_id> AND <max_id>
  AND uph.price < 0
  AND pc.service_id = 3`,
  },
];

/**
 * 個別の SQL クエリをファイルとしてフォーマットする。
 * @graph-connects none
 */
export function buildIndividualSqlText(query: (typeof RAW_DATA_QUERIES)[number]): string {
  return [
    '-- =============================================================',
    `-- ${query.label}`,
    `-- DB: ${query.database}`,
    '-- snapshot of: apps/generator/ac-mens-hub-daily/src/revenue/queries.ts',
    '-- =============================================================',
    '',
    query.sql,
    '',
  ].join('\n');
}

/**
 * 全SQL クエリを1つのテキストファイルとして結合する。
 * @graph-connects none
 */
export function buildSqlExportText(): string {
  const lines = [
    '-- =============================================================',
    "-- aC Men's Hub 売上集計 rawデータ抽出SQL",
    '-- snapshot of: apps/generator/ac-mens-hub-daily/src/revenue/queries.ts',
    '-- =============================================================',
    '',
  ];

  for (const q of RAW_DATA_QUERIES) {
    lines.push('-- -----------------------------------------------------------');
    lines.push(`-- ${q.label}`);
    lines.push(`-- DB: ${q.database}`);
    lines.push('-- -----------------------------------------------------------');
    lines.push(q.sql);
    lines.push('');
  }

  return lines.join('\n');
}

/**
 * 売上集計ロジックの説明テキストを生成する。
 * FIFO対応付け・前受金計算等のロジックを経理チーム向けに記述する。
 * @graph-connects none
 */
export function buildAggregationLogicText(): string {
  return `=============================================================
aC Men's Hub 売上集計ロジック説明
=============================================================

■ 概要
3つのDB（aircloset_mens / phoenix_order / payment）から日次バッチで
rawデータを取得し、FIFO対応付けと前受金計算を行い、日次12指標を算出する。

■ データソース
1. aircloset_mens (PostgreSQL)
   - payment_histories: 課金履歴（purchase_date, original_price, payment_type_code）
   - user_ticket_relations: チケット情報（ticket_type, is_used, expired_at, order_id）
   - user_subscription_plan_relations: 退会情報（cancelled_at）
   - orders: 注文IDマッピング（id → phoenix_order_id）

2. phoenix_order (MySQL)
   - delivery_orders: 発送完了情報（delivered_date, order_status=7で完了判定）
   - orders: service_id=3 でメンズフィルタ

3. payment (MySQL)
   - user_payment_histories: 決済ステータス（paid_status=2,paid_flag=1で確定）
   - cancel_payment_histories: 返金情報

■ 集計ロジック

【売上（フロー指標）】

1. 会費売上(月1回) — subscriptionFeeMonthly
   収益認識: 役務提供完了基準
   ロジック:
   - payment_type_code='subscription_fee' かつ plan_frequency='monthly' の課金と
     ticket_type='subscription' かつ plan_frequency='monthly' のチケットを
     ユーザーごとにFIFO（先入先出法）で1:1対応付け
   - 対応付けたチケットが is_used=true かつ発送完了（delivered_date有り）
     → 発送完了日を売上認識日として課金額を計上

2. 会費売上(2ヶ月1回) — subscriptionFeeBimonthly
   収益認識: 課金確定時一括認識
   ロジック:
   - payment_type_code='subscription_fee' かつ plan_frequency='bimonthly'
   - 決済確定（paid_status=2, paid_flag=1）した課金を購入日で日次合計

3. チケット購入売上 — ticketPurchaseRevenue
   収益認識: 役務提供完了基準
   ロジック:
   - ticket_type='purchase' のチケットは payment_history_id で課金に直結（FIFO不要）
   - is_used=true かつ発送完了 → 発送完了日を売上認識日

4. 違約金売上 — penaltyFee
   収益認識: 課金確定時一括認識
   ロジック: payment_type_code='penalty_fee' の確定済み課金を購入日で日次合計

5. 販売売上 — itemPurchase
   収益認識: 引渡基準
   ロジック: payment_type_code='item_purchase' の確定済み課金を購入日で日次合計

6. 失効売上 — expiredRevenue
   収益認識: 権利失効時認識
   ロジック:
   - FIFO対応付け済みのチケットのうち is_used=false かつ expired_at 設定済み
   - 失効日 = MIN(cancelled_at, expired_at)（退会が先なら退会日で即時失効）
   - 失効日を売上認識日として対応する課金額を計上

7. 貸倒 — badDebt
   ロジック: paid_status=3 AND paid_flag=0（決済失敗）の課金を購入日で日次合計
   ※ 純売上から控除

8. 返金・キャンセル — refund
   ロジック:
   - cancel_payment_histories の canceled_date + ABS(price)
   - user_payment_histories の price < 0 行（Amazon v1.1返金）
   ※ 純売上から控除

【前受金（4指標）】

9. 前受金流入(会費) — deferredInflowSubscription (flow)
   月1回プランの確定済み課金額を購入日で日次合計

10. 前受金流入(チケット購入) — deferredInflowTicket (flow)
    チケット購入の確定済み課金額を購入日で日次合計

11. 前受金残高(会費) — deferredBalanceSubscription (stock)
    恒等式: 残高[d] = 累計流入[0..d] - 累計会費売上[0..d] - 累計失効売上(会費分)[0..d]

12. 前受金残高(チケット購入) — deferredBalanceTicket (stock)
    恒等式: 残高[d] = 累計チケット流入[0..d] - 累計チケット購入売上[0..d] - 累計失効売上(チケット分)[0..d]

■ 月次集計ルール
- フロー指標（type=flow）: 月内の日次値を合計
- ストック指標（type=stock）: 月末日の値を採用

■ 純売上の計算
純売上 = 売上6カテゴリ合計 - 貸倒 - 返金
売上6カテゴリ = 会費売上(月1回) + 会費売上(2ヶ月1回) + チケット購入売上
              + 違約金売上 + 販売売上 + 失効売上
`;
}
